import { useState, useEffect } from 'react';
import { Plus, CreditCard, ArrowRight, ChevronLeft } from 'lucide-react';
// Assuming Modal is imported from a standard path
import Modal from '../components/Modal';
import { useNavigate } from 'react-router-dom';
import useBank from '../hooks/useBank';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

const initialAccountForm = {
    accountName: '',
    accountNumber: '',
    ifscCode: '',
    accountType: 'Savings',
};
const BankAccountsPage = () => {
    // UI State Management
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState(initialAccountForm);
    const [accounts, setAccounts] = useState([]);
    const navigate = useNavigate();
    const { addBank, getBankDetails, loading } = useBank();

    const fetchAccounts = async () => {
        try {
            const data = await getBankDetails();
            if (data.success) {
                setAccounts(data.data || []);
            } else if (data.banks) {
                setAccounts(data.banks);
            }
        } catch (error) {
            console.error("Failed to fetch accounts", error);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {

            const response = await addBank(formData);

            if (response && (response.msg === 'Bank details added successfully' || response.bank)) {
                toast.success("Bank account added successfully!");
                setShowModal(false);
                setFormData(initialAccountForm); // Reset form
                fetchAccounts(); // Refresh list
            } else {
                toast.error(response.message || response.msg || "Failed to add bank account");
            }
        } catch (error) {
            console.error("Error adding bank account", error);
            toast.error(error.message || "Something went wrong!");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50  p-8">
            <div className="absolute top-2 left-3">
                {/* Back Button */}
                <button
                    onClick={() => navigate("/dashboard")}
                    className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors group"
                >
                    <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                    <span className="font-medium">Back to Dashboard</span>
                </button>
            </div>
            <div className="max-w-6xl mx-auto space-y-6">

                {/* --- HEADER AND ACTION BUTTON --- */}
                <div className="flex justify-between items-center pt-4 pb-4 border-b border-gray-200">
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                        Bank Account Management
                    </h1>
                    <button
                        onClick={() => setShowModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition-colors"
                    >
                        <Plus size={20} />
                        Add New Account
                    </button>
                </div>

                {/* --- ACCOUNT CARDS GRID --- */}
                {loading && accounts.length === 0 ? (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ">
                        {accounts.map((account) => (
                            <div
                                key={account._id || account.id}
                                onClick={() => navigate(`bank/${account._id}`)}
                                className="bg-white hover:scale-105 duration-300 cursor-pointer p-6 rounded-xl shadow-lg border border-gray-200 space-y-4"
                            >
                                <div className="flex items-center gap-3">
                                    <CreditCard size={24} className="text-blue-600" />
                                    <h3 className="text-lg font-bold text-gray-900">
                                        {account.accountName}
                                    </h3>
                                </div>

                                <dl className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                        <dt className="text-gray-500">Account No.</dt>
                                        <dd className="font-mono text-gray-700">{account.accountNumber}</dd>
                                    </div>
                                    <div className="flex justify-between">
                                        <dt className="text-gray-500">IFSC Code</dt>
                                        <dd className="font-mono text-gray-700">{account.ifscCode}</dd>
                                    </div>
                                    <div className="flex justify-between">
                                        <dt className="text-gray-500">Type</dt>
                                        <dd className="font-mono text-gray-700">{account.accountType}</dd>
                                    </div>
                                </dl>

                                <div className="relative pt-3 border-t border-gray-100">
                                    <button className="flex items-center gap-1 text-sm font-semibold text-green-600">
                                        <span className='text-lg text-gray-500'>Balance</span>  <span className="text-lg font-extrabold"> {account.balance ? account.balance.toLocaleString('INR') : '0'}</span>

                                    </button>
                                    <div className="absolute bottom-0 right-2 cursor-pointer  p-1 bg-gray-50 rounded-full hover:bg-blue-100 transition-colors">
                                        <ArrowRight size={20} className="text-gray-400 group-hover:text-blue-600" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && accounts.length === 0 && (
                    <div className="text-center py-20 bg-white rounded-xl shadow-md border border-dashed border-gray-300">
                        <p className="text-lg text-gray-500">No bank accounts added yet.</p>
                        <p className="text-sm text-gray-400 mt-2">Click 'Add New Account' to link a bank account.</p>
                    </div>
                )}

            </div>

            {/* 🛑 ADD ACCOUNT MODAL FORM 🛑 */}
            <Modal
                title="Link New Bank Account"
                isOpen={showModal}
                onClose={() => setShowModal(false)}
            >
                <form onSubmit={handleFormSubmit} className="space-y-6">

                    <div className="space-y-4">

                        {/* Bank Name (Mapped to accountName) */}
                        <div>
                            <label htmlFor="accountName" className="text-sm font-medium text-gray-700">Bank Name <span className="text-red-500">*</span></label>
                            <input
                                id="accountName"
                                type="text"
                                name="accountName"
                                value={formData.accountName}
                                onChange={handleFormChange}
                                placeholder="e.g., HDFC Bank, SBI, ICICI"
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                required
                            />
                        </div>

                        {/* Account Number */}
                        <div>
                            <label htmlFor="accountNumber" className="text-sm font-medium text-gray-700">Account Number <span className="text-red-500">*</span></label>
                            <input
                                id="accountNumber"
                                type="text"
                                name="accountNumber"
                                value={formData.accountNumber}
                                onChange={handleFormChange}
                                placeholder="Enter full account number"
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                required
                            />
                        </div>

                        {/* IFSC */}
                        <div>
                            <label htmlFor="ifscCode" className="text-sm font-medium text-gray-700">IFSC Code <span className="text-red-500">*</span></label>
                            <input
                                id="ifscCode"
                                type="text"
                                name="ifscCode"
                                value={formData.ifscCode}
                                onChange={handleFormChange}
                                placeholder="e.g., HDFC0001234"
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                required
                            />
                        </div>

                        {/* Account Type */}
                        <div>
                            <label htmlFor="accountType" className="text-sm font-medium text-gray-700">Account Type <span className="text-red-500">*</span></label>
                            <select
                                id="accountType"
                                name="accountType"
                                value={formData.accountType}
                                onChange={handleFormChange}
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all bg-white"
                                required
                            >
                                <option value="Savings">Savings</option>
                                <option value="Current">Current</option>
                            </select>
                        </div>


                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-md transition-all disabled:opacity-50"
                        >
                            {loading ? 'Saving...' : 'Save Account'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default BankAccountsPage;